import * as React from 'react';
import { FormControl, InputLabel, MenuItem, Typography } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import CountryFlag from 'react-country-flag';
import { getCurrencyInfo } from 'gmd-utilities-package';

export type CurrencySelectProps = {
  id: string;
  label: string;
  codes: string[];
  value?: string;
  onChange: (newValue: string) => void;
  disabled?: boolean;
};

const createOption = (code: string) => {
  const info = getCurrencyInfo(code);
  if (info === null || typeof info === 'undefined') return null;
  return {
    value: code,
    label: info.displayLabel,
    icon: (
      <CountryFlag
        countryCode={info.imageCode}
        style={{ fontSize: '1.5em', lineHeight: '1.5em' }}
        aria-label={info.label}
      />
    ),
  };
};

const CurrencySelect = ({
  id,
  label,
  codes,
  value,
  onChange,
  disabled = false,
}: CurrencySelectProps) => {
  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value || '');
  };
  const options = React.useMemo(() => {
    const results: {
      icon: JSX.Element;
      label: string;
      value: string;
    }[] = [];
    codes.forEach((code) => {
      const option = createOption(code);
      if (option !== null) {
        results.push(option);
      }
    });
    return results.sort((first, second) => {
      const firstLabel = first.label.toUpperCase();
      const secondLabel = second.label.toUpperCase();
      if (firstLabel < secondLabel) {
        return -1;
      }
      if (firstLabel > secondLabel) {
        return 1;
      }
      return 0;
    });
  }, [codes]);

  return (
    <FormControl required fullWidth>
      <InputLabel id={`label-${id}`} style={{ left: -15 }}>
        {label}
      </InputLabel>
      <Select
        variant="standard"
        labelId={`label-${id}`}
        name={id}
        id={id}
        label={label}
        value={value}
        disabled={disabled}
        onChange={handleChange}
      >
        {options.map((option) => (
          <MenuItem value={option.value} key={option.value}>
            {option.icon}
            <Typography sx={{ ml: 1, display: 'inline-block' }}>
              {option.label}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CurrencySelect;
